/**
 * SupabaseSync — persists firms, client metadata, ledger data, and import logs to Supabase.
 *
 * Requires FOUR tables in your Supabase project (run the SQL migration below):
 *
 *   -- 0. Firms / Accountant practices (one row per firm)
 *   CREATE TABLE IF NOT EXISTS firms (
 *     id              TEXT        NOT NULL,
 *     user_id         UUID        NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
 *     name            TEXT        NOT NULL,
 *     owner_name      TEXT,
 *     email           TEXT,
 *     phone           TEXT,
 *     province        TEXT,
 *     license_number  TEXT,
 *     color           TEXT        DEFAULT '#8b5cf6',
 *     created_at      TIMESTAMPTZ DEFAULT NOW(),
 *     updated_at      TIMESTAMPTZ DEFAULT NOW(),
 *     PRIMARY KEY (id, user_id)
 *   );
 *   ALTER TABLE firms ENABLE ROW LEVEL SECURITY;
 *   CREATE POLICY "Users manage own firms" ON firms
 *     FOR ALL USING (auth.uid() = user_id) WITH CHECK (auth.uid() = user_id);
 *
 *   -- 3. Import / parser logs (one row per file upload attempt)
 *   CREATE TABLE IF NOT EXISTS import_logs (
 *     id          UUID        PRIMARY KEY,
 *     user_id     UUID        NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
 *     client_id   TEXT,
 *     filename    TEXT        NOT NULL,
 *     file_size   BIGINT,
 *     file_type   TEXT,
 *     timestamp   BIGINT,
 *     end_time    BIGINT,
 *     status      TEXT,
 *     tx_count    INTEGER     DEFAULT 0,
 *     bank        TEXT,
 *     parser      TEXT,
 *     logs        JSONB       DEFAULT '[]'::jsonb,
 *     errors      JSONB       DEFAULT '[]'::jsonb,
 *     warnings    JSONB       DEFAULT '[]'::jsonb,
 *     created_at  TIMESTAMPTZ DEFAULT NOW()
 *   );
 *   ALTER TABLE import_logs ENABLE ROW LEVEL SECURITY;
 *   CREATE POLICY "Users manage own import logs" ON import_logs
 *     FOR ALL USING (auth.uid() = user_id) WITH CHECK (auth.uid() = user_id);
 *   CREATE INDEX IF NOT EXISTS import_logs_user_status ON import_logs (user_id, status);
 *   CREATE INDEX IF NOT EXISTS import_logs_user_created ON import_logs (user_id, created_at DESC);
 *
 *   -- 1. Client metadata
 *   CREATE TABLE IF NOT EXISTS client_profiles (
 *     id              TEXT NOT NULL,
 *     user_id         UUID NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
 *     name            TEXT NOT NULL,
 *     legal_name      TEXT,
 *     industry        TEXT,
 *     province        TEXT    DEFAULT 'AB',
 *     fiscal_year_end INTEGER DEFAULT 12,
 *     currency        TEXT    DEFAULT 'CAD',
 *     gst_number      TEXT,
 *     color           TEXT    DEFAULT '#3b82f6',
 *     accountant_id   TEXT,
 *     created         DATE,
 *     last_active     DATE,
 *     tx_count        INTEGER DEFAULT 0,
 *     account_count   INTEGER DEFAULT 0,
 *     created_at      TIMESTAMPTZ DEFAULT NOW(),
 *     updated_at      TIMESTAMPTZ DEFAULT NOW(),
 *     PRIMARY KEY (id, user_id)
 *   );
 *   ALTER TABLE client_profiles ENABLE ROW LEVEL SECURITY;
 *   CREATE POLICY "Users manage own clients" ON client_profiles
 *     FOR ALL USING (auth.uid() = user_id) WITH CHECK (auth.uid() = user_id);
 *
 *   -- 2. Ledger data (full snapshot as JSONB)
 *   CREATE TABLE IF NOT EXISTS client_ledger_data (
 *     client_id  TEXT  NOT NULL,
 *     user_id    UUID  NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
 *     data       JSONB NOT NULL DEFAULT '{}',
 *     updated_at TIMESTAMPTZ DEFAULT NOW(),
 *     PRIMARY KEY (client_id, user_id)
 *   );
 *   ALTER TABLE client_ledger_data ENABLE ROW LEVEL SECURITY;
 *   CREATE POLICY "Users manage own ledger data" ON client_ledger_data
 *     FOR ALL USING (auth.uid() = user_id) WITH CHECK (auth.uid() = user_id);
 *
 * Strategy: LOCAL-FIRST
 *   - Reads always come from IndexedDB / localStorage (fast, synchronous).
 *   - Writes go to both local AND Supabase (async, fire-and-forget).
 *   - On init / switchClient, if local data is missing, a cloud fallback is
 *     triggered to restore from Supabase (handles browser-cleared scenarios).
 *   - Ledger saves are debounced to at most one cloud write per 10 seconds
 *     to avoid hammering Supabase on every keystroke / import.
 */

import { supabase } from '../lib/supabaseClient.js';

const LEDGER_SYNC_DEBOUNCE_MS = 10_000; // max 1 cloud ledger write per 10 s
const _debounceTimers = {};              // clientId → setTimeout handle

const SupabaseSync = {

    // ── Internal: get current authenticated user ────────────────────────────
    async _getUser() {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            return user || null;
        } catch { return null; }
    },

    // ════════════════════════════════════════════════════════════════════════
    // FIRMS (ACCOUNTANT PRACTICES)
    // ════════════════════════════════════════════════════════════════════════

    /** Upsert a firm row. Called after every create/edit. */
    async saveAccountant(accountant) {
        const user = await this._getUser();
        if (!user || !accountant?.id) return;

        const { error } = await supabase.from('firms').upsert({
            id:             accountant.id,
            user_id:        user.id,
            name:           accountant.name,
            owner_name:     accountant.ownerName     || null,
            email:          accountant.email         || null,
            phone:          accountant.phone         || null,
            province:       accountant.province      || null,
            license_number: accountant.licenseNumber || null,
            color:          accountant.color         || '#8b5cf6',
            updated_at:     new Date().toISOString(),
        }, { onConflict: 'id,user_id' });

        if (error) console.error('[SUPABASE_SYNC] saveAccountant error:', error.message);
        else       console.log('[SUPABASE_SYNC] Firm saved to cloud:', accountant.id, accountant.name);
    },

    /** Fetch all firms for the current user from Supabase. */
    async loadAccountants() {
        const user = await this._getUser();
        if (!user) return null;

        const { data, error } = await supabase
            .from('firms')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: true });

        if (error) {
            console.error('[SUPABASE_SYNC] loadAccountants error:', error.message);
            return null;
        }

        return (data || []).map(row => ({
            id:            row.id,
            name:          row.name,
            ownerName:     row.owner_name     || '',
            email:         row.email          || '',
            phone:         row.phone          || '',
            province:      row.province       || '',
            licenseNumber: row.license_number || '',
            color:         row.color          || '#8b5cf6',
            created:       row.created_at,
            lastActive:    row.updated_at,
        }));
    },

    /** Delete a firm from Supabase. Clients must be reassigned before deletion. */
    async deleteAccountant(accountantId) {
        const user = await this._getUser();
        if (!user || !accountantId) return;

        const { error } = await supabase.from('firms')
            .delete().eq('id', accountantId).eq('user_id', user.id);

        if (error) console.error('[SUPABASE_SYNC] deleteAccountant error:', error.message);
        else       console.log('[SUPABASE_SYNC] Firm deleted from cloud:', accountantId);
    },

    // ════════════════════════════════════════════════════════════════════════
    // CLIENT METADATA
    // ════════════════════════════════════════════════════════════════════════

    /** Upsert a client profile row. Call after every create/edit. */
    async saveClient(client) {
        const user = await this._getUser();
        if (!user || !client?.id) return;

        const { error } = await supabase.from('client_profiles').upsert({
            id:              client.id,
            user_id:         user.id,
            name:            client.name,
            legal_name:      client.legalName      || null,
            industry:        client.industry       || null,
            province:        client.province       || 'AB',
            fiscal_year_end: client.fiscalYearEnd  ?? 12,
            currency:        client.currency       || 'CAD',
            gst_number:      client.gstNumber      || null,
            color:           client.color          || '#3b82f6',
            accountant_id:   client.accountantId   || null,
            created:         client.created        || null,
            last_active:     client.lastActive     || null,
            tx_count:        client.txCount        ?? 0,
            account_count:   client.accountCount   ?? 0,
            updated_at:      new Date().toISOString(),
        }, { onConflict: 'id,user_id' });

        if (error) console.error('[SUPABASE_SYNC] saveClient error:', error.message);
        else       console.log('[SUPABASE_SYNC] Client saved to cloud:', client.id, client.name);
    },

    /**
     * Fetch all clients for the current user from Supabase.
     * Returns null on error or when not logged in.
     */
    async loadClients() {
        const user = await this._getUser();
        if (!user) return null;

        const { data, error } = await supabase
            .from('client_profiles')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: true });

        if (error) {
            console.error('[SUPABASE_SYNC] loadClients error:', error.message);
            return null;
        }

        // Map snake_case DB columns back to camelCase app objects
        return (data || []).map(row => ({
            id:            row.id,
            name:          row.name,
            legalName:     row.legal_name,
            industry:      row.industry,
            province:      row.province,
            fiscalYearEnd: row.fiscal_year_end,
            currency:      row.currency,
            gstNumber:     row.gst_number,
            color:         row.color,
            accountantId:  row.accountant_id,
            created:       row.created,
            lastActive:    row.last_active,
            txCount:       row.tx_count    || 0,
            accountCount:  row.account_count || 0,
        }));
    },

    /** Delete a client profile and its ledger data from Supabase. */
    async deleteClient(clientId) {
        const user = await this._getUser();
        if (!user || !clientId) return;

        // Cancel any pending debounced ledger sync
        clearTimeout(_debounceTimers[clientId]);
        delete _debounceTimers[clientId];

        // Delete ledger data first
        await supabase.from('client_ledger_data')
            .delete().eq('client_id', clientId).eq('user_id', user.id);

        const { error } = await supabase.from('client_profiles')
            .delete().eq('id', clientId).eq('user_id', user.id);

        if (error) console.error('[SUPABASE_SYNC] deleteClient error:', error.message);
        else       console.log('[SUPABASE_SYNC] Client deleted from cloud:', clientId);
    },

    // ════════════════════════════════════════════════════════════════════════
    // LEDGER DATA
    // ════════════════════════════════════════════════════════════════════════

    /**
     * Schedule a debounced cloud ledger save.
     * Rapid calls (e.g. categorising 500 rows) coalesce into one write.
     * Called from ledger.core.js save() on every local write.
     */
    scheduleLedgerSave(clientId, data) {
        if (!clientId || !data) return;
        clearTimeout(_debounceTimers[clientId]);
        _debounceTimers[clientId] = setTimeout(() => {
            this.saveLedger(clientId, data);
        }, LEDGER_SYNC_DEBOUNCE_MS);
    },

    /** Immediately write a ledger snapshot to Supabase. */
    async saveLedger(clientId, data) {
        const user = await this._getUser();
        if (!user || !clientId || !data) return;

        const { error } = await supabase.from('client_ledger_data').upsert({
            client_id:  clientId,
            user_id:    user.id,
            data:       data,
            updated_at: new Date().toISOString(),
        }, { onConflict: 'client_id,user_id' });

        if (error) console.error('[SUPABASE_SYNC] saveLedger error:', error.message);
        else       console.log('[SUPABASE_SYNC] Ledger synced to cloud:', clientId);
    },

    /**
     * Fetch a ledger snapshot for a client from Supabase.
     * Returns the parsed data object, or null if not found / not logged in.
     */
    async loadLedger(clientId) {
        const user = await this._getUser();
        if (!user || !clientId) return null;

        const { data, error } = await supabase
            .from('client_ledger_data')
            .select('data')
            .eq('client_id', clientId)
            .eq('user_id', user.id)
            .single();

        if (error) {
            // PGRST116 = no rows — expected if client was never synced
            if (error.code !== 'PGRST116') {
                console.error('[SUPABASE_SYNC] loadLedger error:', error.message);
            }
            return null;
        }

        return data?.data || null;
    },

    // ════════════════════════════════════════════════════════════════════════
    // IMPORT / PARSER LOGS
    // ════════════════════════════════════════════════════════════════════════

    /**
     * Persist one import session to Supabase (fire-and-forget).
     * Called by ImportLogger.endSession() on every upload (success or failure).
     *
     * Logs array is capped at 500 entries before storing to keep row sizes sane;
     * the full log is always available in the browser's IndexedDB copy.
     */
    async saveImportLog(session) {
        const user = await this._getUser();
        if (!user || !session?.id) return;

        // Cap log lines to 500 per session (oldest dropped first) to keep JSONB rows sane.
        // Full uncapped log is always in IndexedDB for local access.
        const cappedLogs = session.logs.length > 500
            ? session.logs.slice(-500)
            : session.logs;

        const { error } = await supabase.from('import_logs').upsert({
            id:         session.id,
            user_id:    user.id,
            client_id:  window.UI_STATE?.activeClientId || null,
            filename:   session.filename,
            file_size:  session.fileSize   || 0,
            file_type:  session.fileType   || 'other',
            timestamp:  session.timestamp,
            end_time:   session.endTime,
            status:     session.status,
            tx_count:   session.txCount    || 0,
            bank:       session.bank       || null,
            parser:     session.parser     || null,
            logs:       cappedLogs,
            errors:     session.errors,
            warnings:   session.warnings,
        }, { onConflict: 'id' });

        if (error) console.warn('[SUPABASE_SYNC] saveImportLog failed:', error.message);
        else       console.log('[SUPABASE_SYNC] Import log saved:', session.filename, session.status);
    },

    /**
     * Fetch recent import logs for the current user from Supabase.
     * @param {object} opts  — { limit: 50, status: 'error'|'warning'|null }
     * Returns array of session objects (newest first), or null on error.
     */
    async loadImportLogs({ limit = 50, status = null } = {}) {
        const user = await this._getUser();
        if (!user) return null;

        let query = supabase
            .from('import_logs')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(limit);

        if (status) query = query.eq('status', status);

        const { data, error } = await query;

        if (error) {
            console.error('[SUPABASE_SYNC] loadImportLogs error:', error.message);
            return null;
        }

        // Map back to ImportLogger session shape
        return (data || []).map(row => ({
            id:        row.id,
            filename:  row.filename,
            fileSize:  row.file_size,
            fileType:  row.file_type,
            timestamp: row.timestamp,
            endTime:   row.end_time,
            status:    row.status,
            txCount:   row.tx_count,
            bank:      row.bank       || '',
            parser:    row.parser     || '',
            clientId:  row.client_id  || null,
            logs:      row.logs       || [],
            errors:    row.errors     || [],
            warnings:  row.warnings   || [],
        }));
    },
};

// Expose globally so vanilla JS shell (app.js, ledger.core.js) can access it
window.SupabaseSync = SupabaseSync;

export default SupabaseSync;
